import { fg } from '@atlaskit/platform-feature-flags';

import {
	ASSETS_INTERNAL_WORKSPACE_RESOLVER_URL,
	ASSETS_WORKSPACE_HOST_FG,
	ASSETS_WORKSPACE_RESOLVER_URL,
	DEFAULT_PRODUCT_CONTEXT,
	type ProductContext,
} from '../common/constants';
import type {
	AssetsWorkspaceHost,
	FetchAssetsWorkspaceHostOptions,
	WorkspaceLatestResponse,
} from '../common/types';
import {
	getInFlight,
	getSnapshot,
	isStale,
	setError,
	setPending,
	setSuccess,
	setUnavailable,
} from '../state/store';
import { buildAbsoluteAssetsUrl } from '../utils/build-absolute-assets-url';

class AssetsWorkspaceHostUnavailableError extends Error {
	constructor(message: string) {
		super(message);
		this.name = 'AssetsWorkspaceHostUnavailableError';
	}
}

const toResolvedHost = (response: WorkspaceLatestResponse): AssetsWorkspaceHost => {
	const host = response.siteWithJiraUrl ?? null;
	return Object.freeze({
		workspaceId: response.linkedAssetsWorkspaceId,
		host,
		cmdbSiteCloudId: response.linkedAssetsSiteCloudId ?? null,
		toAbsoluteUrl: (relativePath) => buildAbsoluteAssetsUrl(host, relativePath),
	});
};

const performFetch = async (cloudId: string, endpoint: string): Promise<AssetsWorkspaceHost> => {
	let response: Response;
	try {
		response = await fetch(endpoint, {
			method: 'GET',
			credentials: 'include',
			headers: {
				accept: 'application/json',
				'x-atlassian-cloud-id': cloudId,
			},
		});
	} catch (networkError) {
		const wrapped =
			networkError instanceof Error ? networkError : new Error('Network request failed');
		setError(cloudId, wrapped);
		throw wrapped;
	}

	if (response.status === 401 || response.status === 403 || response.status === 404) {
		// 401/403 = no entitlement; 404 = tenant has no Assets workspace at all.
		// In every case the consumer should treat Assets as unavailable on this
		// site and fall back to current-site links / hide CTAs.
		setUnavailable(cloudId);
		throw new AssetsWorkspaceHostUnavailableError(
			`Assets workspace resolver returned ${response.status}`,
		);
	}

	if (!response.ok) {
		const error = new Error(`Assets workspace resolver returned ${response.status}`);
		setError(cloudId, error);
		throw error;
	}

	let parsed: WorkspaceLatestResponse;
	try {
		parsed = (await response.json()) as WorkspaceLatestResponse;
	} catch (parseError) {
		const wrapped =
			parseError instanceof Error ? parseError : new Error('Failed to parse response');
		setError(cloudId, wrapped);
		throw wrapped;
	}

	if (!parsed?.linkedAssetsWorkspaceId) {
		const error = new Error('Assets workspace resolver returned no linkedAssetsWorkspaceId');
		setError(cloudId, error);
		throw error;
	}

	const resolved = toResolvedHost(parsed);
	setSuccess(cloudId, resolved);
	return resolved;
};

/**
 * Imperative, single-flight fetcher used by both the React hook and any
 * non-React consumer (e.g. smart-link datasource, Forge bridges).
 *
 * Returns the cached value when fresh; coalesces concurrent callers onto a
 * single network request; respects the `astral_units_workspace_host_resolver`
 * feature gate.
 *
 * `cloudId` is mandatory — every cache entry is keyed by it, so callers must
 * explicitly choose which tenant they are resolving for.
 */

/**
 * Build the endpoint URL based on `isOnlyWorkspace` + `productContext`.
 * `endpointOverride` always wins (escape hatch for dev hosts).
 */
export const buildEndpoint = (
	isOnlyWorkspace: boolean,
	productContext: ProductContext,
	endpointOverride?: string,
): string => {
	if (endpointOverride) {
		return endpointOverride;
	}
	if (isOnlyWorkspace) {
		return ASSETS_WORKSPACE_RESOLVER_URL;
	}
	return `${ASSETS_INTERNAL_WORKSPACE_RESOLVER_URL}?productContext=${encodeURIComponent(
		productContext,
	)}`;
};

export const fetchAssetsWorkspaceHost = async (
	options: FetchAssetsWorkspaceHostOptions,
): Promise<AssetsWorkspaceHost> => {
	const { cloudId } = options;
	const isOnlyWorkspace = options.isOnlyWorkspace === true;
	const productContext = options.productContext ?? DEFAULT_PRODUCT_CONTEXT;
	const endpoint = buildEndpoint(isOnlyWorkspace, productContext, options.endpoint);
	const force = options.force === true;

	if (!fg('astral_units_workspace_host_resolver')) {
		setUnavailable(cloudId);
		throw new AssetsWorkspaceHostUnavailableError(
			`Feature gate ${ASSETS_WORKSPACE_HOST_FG} is disabled`,
		);
	}

	if (!force) {
		const inFlight = getInFlight(cloudId);
		if (inFlight) {
			return inFlight;
		}
		const snapshot = getSnapshot(cloudId);
		if (snapshot.status === 'success' && !isStale(cloudId)) {
			return snapshot.data;
		}
	}

	const promise = performFetch(cloudId, endpoint);
	setPending(cloudId, promise);
	return promise;
};

export { AssetsWorkspaceHostUnavailableError };
